/**
 * One driver's rows for the phone driver list (#2677). Pure: no React, no
 * i18n, so the desktop route sidebar can reuse it (#2681).
 *
 * A row is one driver task. With consolidation on, one task carries several
 * same-address delivery jobs (ADR-0026 D2), so a row holds every job id it
 * covers. The pending order is the join's: ETA ascending, unknown last. That
 * is NOT the route order the driver runs (the ETA report sorts by estimated
 * completion), so nothing here numbers the rows.
 */
import type { MappedDriverRoute, MappedDriverTask } from '../hooks/useRouteMonitoringJoin';

export interface StopRow {
  taskId: string;
  /** Every delivery job this task covers; empty when it reached no stop. */
  jobIds: string[];
  orderNames: string[];
  /** The ETA report's own order label, for a task that reached no stop. */
  fallbackOrderName: string | null;
  /** Estimated completion (unix ms); `null` when unknown or delivered. */
  etaMs: number | null;
  /** Completion time (unix ms); `null` until delivered or when unknown. */
  completedAt: number | null;
  active: boolean;
  /** The ETA is after the window end — the one late rule (`enRouteModel`). */
  late: boolean;
}

export interface DriverStops {
  pending: StopRow[];
  done: StopRow[];
  /** Delivery jobs on late pending rows: a task covering k jobs counts k, none counts 1. */
  lateCount: number;
}

/** The delivery jobs one row stands for (a task with no stop still is one). */
export function rowJobCount(row: Pick<StopRow, 'jobIds'>): number {
  return Math.max(1, row.jobIds.length);
}

function toRow(task: MappedDriverTask, windowEndMs: number | null): StopRow {
  const etaMs = task.estimatedCompletionTime;
  return {
    taskId: task.taskId,
    jobIds: task.stops.map((stop) => stop.job_id),
    orderNames: task.stops.map((stop) => stop.order_name),
    fallbackOrderName: task.orderName,
    etaMs,
    completedAt: task.completedAt,
    active: task.state === 'active',
    late: windowEndMs !== null && etaMs !== null && etaMs > windowEndMs,
  };
}

/** Ascending, unknown times last. */
function byTimeAsc(left: number | null, right: number | null): number {
  if (left === null && right === null) return 0;
  if (left === null) return 1;
  if (right === null) return -1;
  return left - right;
}

export function buildDriverStops(
  route: MappedDriverRoute,
  windowEndMs: number | null,
): DriverStops {
  const pending = route.tasks
    .filter((task) => task.state !== 'completed')
    .map((task) => toRow(task, windowEndMs));
  const done = route.tasks
    .filter((task) => task.state === 'completed')
    .map((task) => toRow(task, windowEndMs))
    .sort((a, b) => byTimeAsc(a.completedAt, b.completedAt));
  const lateCount = pending
    .filter((row) => row.late)
    .reduce((sum, row) => sum + rowJobCount(row), 0);
  return { pending, done, lateCount };
}
